<script>
import { required } from "vuelidate/lib/validators";
import validationMessages from "@/components/validations";
import Football from "@/services/Football";
import General from "@/services/General";
import Swal from "sweetalert2";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";

const CUSTOM_TEAM_TYPE = "__custom__";

const DEFAULT_TEAM_TYPE_OPTIONS = [
  { value: "first_team", text: "First Team" },
  { value: "women", text: "Women" },
  { value: "young", text: "Young" },
  { value: "youth", text: "Youth" },
];

export default {
  components: { validationMessages, vueDropzone: vue2Dropzone },

  props: {
    tournamentOptions: {
      type: Array,
      default: () => [],
    },
    provider: {
      type: String,
      default: "Azerion",
    },
  },

  data() {
    return {
      team_name: "",
      team_code: "",
      team_country: "",
      team_country_code: "",
      team_type: "women",
      customTeamType: "",
      tournament_id: "",
      team_id: "",
      team_logo: "",
      team_logo_url: "",
      logoSource: "upload",
      countries: [],
      submitted: false,
      tryingToSubmit: false,
      teamTypeOptions: DEFAULT_TEAM_TYPE_OPTIONS.map((option) => ({ ...option })),
      dropzoneOptions: {
        url: "/",
        autoProcessQueue: false,
        addRemoveLinks: true,
        thumbnailWidth: 200,
        thumbnailMethod: "contain",
        maxFilesize: 5,
        acceptedFiles: "image/*",
        maxFiles: 1,
      },
    };
  },

  computed: {
    isCustomTeamType() {
      return this.team_type === CUSTOM_TEAM_TYPE;
    },
    teamTypeSelectOptions() {
      return [
        ...this.teamTypeOptions,
        { value: CUSTOM_TEAM_TYPE, text: "Add custom type..." },
      ];
    },
    logoUrlPreview() {
      return String(this.team_logo_url || "").trim();
    },
  },

  watch: {
    logoSource(value) {
      if (value === "url") {
        this.clearTeamLogoDropzone();
      } else {
        this.team_logo_url = "";
      }
    },
  },

  validations: {
    team_name: { required },
    team_country: { required },
    customTeamType: {
      required(value) {
        if (!this.isCustomTeamType) {
          return true;
        }
        return Boolean(this.normalizeTeamType(value));
      },
    },
  },

  methods: {
    closeModal() {
      this.$bvModal.hide("add_sport_team");
    },

    normalizeTeamType(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_")
        .replace(/[^a-z0-9_]/g, "")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "");
    },

    formatTeamTypeLabel(value) {
      return String(value || "")
        .split("_")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    },

    resolveTeamType() {
      if (this.isCustomTeamType) {
        return this.normalizeTeamType(this.customTeamType);
      }
      return this.team_type;
    },

    ensureTeamTypeOption(value) {
      if (!value || this.teamTypeOptions.some((option) => option.value === value)) {
        return;
      }
      this.teamTypeOptions.push({
        value,
        text: this.formatTeamTypeLabel(value),
      });
    },

    formatError(error) {
      if (!error) return "Failed to create team";
      if (Array.isArray(error)) return error.join(", ");
      return error;
    },

    tournamentOptionLabel(item) {
      if (!item) return "";
      const competition = String(item.competition_name || "").trim();
      const season = String(item.tournament_season || item.tournament_name || "").trim();
      if (competition && season && competition !== season) {
        return `${competition} · ${season}`;
      }
      return competition || season || item.tournament_id || "Tournament";
    },

    async getCountry() {
      try {
        const response = await General.getCountry();
        this.countries = this.preferredCountryOrder(response.data || [], "name");
        this.applyDefaultCountry();
      } catch (error) {
        this.countries = [];
      }
    },

    applyDefaultCountry() {
      if (!this.countries.length) {
        return;
      }
      if (this.team_country_code) {
        const existing = this.countries.find(
          (country) => country.alpha3code === this.team_country_code
        );
        if (existing) {
          this.team_country = existing.name;
          return;
        }
      }
      const preferred =
        this.countries.find((country) => country.alpha3code === "NLD") ||
        this.countries.find((country) => country.alpha3code === "NED") ||
        this.countries.find((country) => /netherlands|nederland/i.test(country.name)) ||
        this.countries[0];
      this.team_country_code = preferred.alpha3code || "";
      this.team_country = preferred.name || "";
    },

    onCountryChange() {
      const selected = this.countries.find(
        (country) => country.alpha3code === this.team_country_code
      );
      this.team_country = selected ? selected.name : "";
    },

    onModalShown() {
      if (!this.countries.length) {
        this.getCountry();
      }
    },

    selectTeamLogo(files) {
      setTimeout(() => {
        if (!this.$refs.teamLogoDropzone) {
          return;
        }
        const queuedFiles = this.$refs.teamLogoDropzone.getQueuedFiles();
        if (queuedFiles.length > 1) {
          Swal.fire("Fail!", "You can only upload one team logo", "warning");
          files.forEach((value) => {
            this.$refs.teamLogoDropzone.removeFile(value);
          });
        }
        this.team_logo = queuedFiles[0] || "";
      }, 250);
    },

    clearTeamLogoDropzone() {
      if (this.$refs.teamLogoDropzone) {
        this.$refs.teamLogoDropzone.removeAllFiles(true);
      }
      this.team_logo = "";
    },

    async createSportTeam() {
      this.tryingToSubmit = true;
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.tryingToSubmit = false;
        return;
      }

      const teamType = this.resolveTeamType();
      if (!teamType) {
        this.tryingToSubmit = false;
        return;
      }

      const payload = {
        team_name: this.team_name,
        team_country: this.team_country,
        team_country_code: this.team_country_code || null,
        team_type: teamType,
        provider: this.provider,
      };

      if (this.team_code) payload.team_code = this.team_code.toUpperCase();
      if (this.team_id) payload.team_id = this.team_id;
      if (this.tournament_id) payload.tournament_id = this.tournament_id;

      if (this.logoSource === "upload" && this.team_logo && this.team_logo.dataURL) {
        payload.team_logo = this.team_logo;
      } else if (this.logoSource === "url" && this.logoUrlPreview) {
        payload.team_logo = this.logoUrlPreview;
      }

      try {
        await Football.createSportTeam(payload);
        this.ensureTeamTypeOption(teamType);
        Swal.fire("Success!", "Team created successfully!", "success");
        this.closeModal();
        this.$emit("onRefresh");
      } catch (error) {
        const err = error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : "";
        Swal.fire("Fail!", this.formatError(err), "warning");
      } finally {
        this.tryingToSubmit = false;
      }
    },

    resetForm() {
      this.team_name = "";
      this.team_code = "";
      this.team_country = "";
      this.team_country_code = "";
      this.team_type = "women";
      this.customTeamType = "";
      this.tournament_id = "";
      this.team_id = "";
      this.logoSource = "upload";
      this.team_logo_url = "";
      this.clearTeamLogoDropzone();
      this.submitted = false;
      this.$v.$reset();
      this.applyDefaultCountry();
    },
  },
};
</script>

<template>
  <b-modal
    id="add_sport_team"
    title="Add Team"
    title-class="font-18"
    centered
    size="lg"
    @shown="onModalShown"
    @hidden="resetForm"
  >
    <form @submit.prevent="createSportTeam">
      <div class="form-group mb-3">
        <label for="team_name">Team Name <span class="text-danger">*</span></label>
        <input
          id="team_name"
          v-model="team_name"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': submitted && $v.team_name.$error }"
        />
        <validationMessages v-if="submitted" :field="$v.team_name" />
      </div>

      <div class="row">
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="team_code">Team Code</label>
            <input
              id="team_code"
              v-model="team_code"
              type="text"
              maxlength="4"
              class="form-control"
            />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="team_type">Team Type</label>
            <b-form-select
              id="team_type"
              v-model="team_type"
              :options="teamTypeSelectOptions"
            />
            <div v-if="isCustomTeamType" class="mt-2">
              <input
                id="custom_team_type"
                v-model="customTeamType"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': submitted && $v.customTeamType.$error }"
                placeholder="e.g. academy or u21"
              />
              <small class="text-muted">
                Saved as snake_case (e.g. <code>u21</code>, <code>academy</code>).
              </small>
              <div
                v-if="submitted && $v.customTeamType.$error"
                class="invalid-feedback d-block"
              >
                Custom team type is required.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="team_country">Country <span class="text-danger">*</span></label>
            <select
              id="team_country"
              v-model="team_country_code"
              class="form-select"
              :class="{ 'is-invalid': submitted && $v.team_country.$error }"
              @change="onCountryChange"
            >
              <option value="">Select country</option>
              <option
                v-for="country in countries"
                :key="country.alpha3code"
                :value="country.alpha3code"
              >
                {{ country.name }}
              </option>
            </select>
            <validationMessages v-if="submitted" :field="$v.team_country" />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="team_country_code">Country Code</label>
            <input
              id="team_country_code"
              v-model="team_country_code"
              type="text"
              maxlength="3"
              class="form-control"
              readonly
            />
          </div>
        </div>
      </div>
      <div class="form-group mb-3">
        <label for="tournament_id">Tournament</label>
        <b-form-select id="tournament_id" v-model="tournament_id">
          <option value="">Select tournament (optional)</option>
          <option
            v-for="item in tournamentOptions"
            :key="item.tournament_id"
            :value="item.tournament_id"
          >
            {{ tournamentOptionLabel(item) }}
          </option>
        </b-form-select>
      </div>

      <div class="form-group mb-3">
        <label for="team_id">Team ID</label>
        <input
          id="team_id"
          v-model="team_id"
          type="text"
          class="form-control"
          placeholder="Auto-generated if empty"
        />
      </div>

      <div class="form-group mb-3">
        <label class="d-block">Team Logo</label>
        <div class="btn-group mb-3" role="group">
          <a
            href="javascript:void(0);"
            class="btn waves-effect waves-light btn-sm"
            :class="logoSource === 'upload' ? 'btn-primary' : 'btn-outline-primary'"
            @click="logoSource = 'upload'"
          >
            Upload image
          </a>
          <a
            href="javascript:void(0);"
            class="btn waves-effect waves-light btn-sm"
            :class="logoSource === 'url' ? 'btn-primary' : 'btn-outline-primary'"
            @click="logoSource = 'url'"
          >
            Use URL
          </a>
        </div>

        <div v-if="logoSource === 'upload'">
          <vue-dropzone
            id="team_logo_dropzone"
            ref="teamLogoDropzone"
            :use-custom-slot="true"
            :options="dropzoneOptions"
            @vdropzone-files-added="selectTeamLogo"
            @vdropzone-removed-file="team_logo = ''"
            no-progress-bar
          >
            <div class="dropzone-custom-content">
              <i class="display-4 text-muted bx bxs-cloud-upload"></i>
              <h4>Drop file here or click to upload.</h4>
            </div>
          </vue-dropzone>
          <small class="text-muted">Optional. Max size: 5 MB. Images only.</small>
        </div>

        <div v-else>
          <input
            id="team_logo_url"
            v-model="team_logo_url"
            type="url"
            class="form-control"
            placeholder="https://example.com/logo.png"
          />
          <small class="text-muted">Optional. Paste an existing logo image URL.</small>
          <div v-if="logoUrlPreview" class="mt-3 text-center">
            <img
              :src="logoUrlPreview"
              alt="Logo preview"
              class="img-thumbnail"
              style="max-width: 160px; max-height: 160px; object-fit: contain;"
              @error="$event.target.style.display = 'none'"
              @load="$event.target.style.display = ''"
            />
          </div>
        </div>
      </div>
    </form>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Close</b-button>
      <b-button variant="primary" @click="createSportTeam" :disabled="tryingToSubmit">
        <b-spinner v-show="tryingToSubmit" small></b-spinner>
        Save
      </b-button>
    </template>
  </b-modal>
</template>
